import React from 'react'
import { ZinButton } from '@tnl/zin-react'
import { useNavigate } from 'react-router-dom'
import SectionTitle from '@components/ui/SectionTitle/SectionTitle'
import { InfoList, type InfoListItem } from '@components/ui/InfoList'
import { Illustration } from '@components/ui/Illustration'
import { IOwner } from '@features/account/types'
import { formatPhoneNumber, toTitleCase, getStateAbbreviation } from '@utils/formattingUtils'
import { useSiteContext } from '@contexts/MkSiteContext'
import type { MyAccountOwnershipPageConfig } from '@pages/account/MyAccount/pageMyAccountOwnershipConfig'
import { useResorts } from '@resort/context/ResortContext'
import { getResortUrlByName } from '@utils/resortUtils'
import { OWNERSHIP_PAGE_TITLE, OWNERSHIP_PAGE_TITLE_DESCRIPTION, PAYMENTUS_URL } from '@constants'
import Card from '@components/ui/Card/Card'
import myContracts from '@data/mockAccountMyContracts.json'
import './MkContracts.scss'

interface MkContractsProps {
  ownershipPageConfig: MyAccountOwnershipPageConfig
  owner: IOwner & {
    contracts?: any[]
    totalPoints?: number
    availablePoints?: number
    totalCredits?: number
    availableCredits?: number
    useByDate?: string
    errorCode?: string
    errorMessage?: string
  }
}

const MkContracts: React.FC<MkContractsProps> = ({ owner, ownershipPageConfig }) => {
  const navigate = useNavigate()
  const { themeBrand: brand } = useSiteContext()
  const { allResorts } = useResorts()

  // Helper function to get formatted city, state, postal code
  const getFormattedCityStatePostal = () => {
    const cityStatePostal = [owner.city ? toTitleCase(owner.city) : null, owner.stateProvince ? getStateAbbreviation(owner.stateProvince) : null, owner.postalCode].filter(Boolean)
    return cityStatePostal.length > 0 ? cityStatePostal.join(', ') : null
  }

  // Helper function to convert contract data to InfoList items
  const ownershipSummaryCardProps =
    brand === 'sir'
      ? {
          inversed: false,
          subtone: false,
          elevated: true,
          className: 'mk-contracts__summary-card',
        }
      : {
          inversed: true,
          subtone: true,
          elevated: true,
          className: 'mk-contracts__summary-card',
          innerClassName: 'mk-contracts__summary-card-inner',
        }

  const createContractInfoList = (contract: any): InfoListItem[] => [
    {
      icon: 'user',
      label: myContracts.labels.user,
      value: contract.owners.join(', '),
    },
    {
      icon: 'file-contract',
      label: myContracts.labels.contractType,
      value: contract.contractType,
    },
    {
      icon: 'calendar',
      label: myContracts.labels.date,
      value: contract.useYearEnd,
    },
    {
      icon: 'award',
      label: ownershipPageConfig.contractLabels.owned,
      value: contract.pointsOwned.toLocaleString(),
    },
    {
      icon: 'cart-shopping',
      label: myContracts.labels.purchaseType,
      value: contract.purchaseType,
    },
  ]

  return (
    <div className="mk-contracts">
      <SectionTitle
        className="mk-contracts__header"
        description={OWNERSHIP_PAGE_TITLE_DESCRIPTION}
        title={OWNERSHIP_PAGE_TITLE}
        titleTag="h2"
      />

      <div className="mk-contracts__grid">
        <div className="mk-contracts__left">
          {owner.contracts?.map((contract) => (
            <div
              key={contract.contractNumber}
              className="mk-contracts__contract-card">
              <div className="mk-contracts__contract-header">
                <h3 className="mk-contracts__contract-number">
                  <span className="body-2">Contract Number: </span>
                  <span className="subtitle-2">{contract.contractNumber}</span>
                </h3>
              </div>

              <div className="mk-contracts__contract-details">
                <InfoList
                  items={createContractInfoList(contract).filter((item) => {
                    const normalizedPurchaseType = typeof contract.purchaseType === 'string' ? contract.purchaseType.toLowerCase() : ''
                    // Always keep non-purchase-type rows
                    if (item.value !== contract.purchaseType) {
                      return true
                    }
                    // Only show Purchase Type row if the contract purchase type is "resale"
                    return normalizedPurchaseType === 'resale'
                  })}
                />
              </div>

              {(ownershipPageConfig.contract.showHomeResort || contract.reciprocalResort) && (
                <div className="mk-contracts__resort-info">
                  {ownershipPageConfig.contract.showHomeResort && (
                    <div className="mk-contracts__resort-box">
                      <h4 className="body-2 font-semibold">Home Resort</h4>
                      <ZinButton
                        variant="link"
                        size="small"
                        onClick={() => navigate(getResortUrlByName(contract.homeResort, allResorts))}>
                        {contract.homeResort}
                      </ZinButton>
                      <p className="mk-contracts__resort-location">{contract.homeResortLocation || 'Location not available'}</p>
                    </div>
                  )}

                  {contract.reciprocalResort && (
                    <div className="mk-contracts__resort-box">
                      <h4 className="body-2 font-semibold">Reciprocal Resort</h4>
                      <ZinButton
                        variant="link"
                        size="small"
                        onClick={() => navigate(getResortUrlByName(contract.reciprocalResort, allResorts))}>
                        {contract.reciprocalResort}
                      </ZinButton>
                      <p className="mk-contracts__resort-location">{contract.reciprocalResortLocation || 'Location not available'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mk-contracts__right flex flex-col gap-8">
          {/* Right column content - Payments CTA & Point of Contact */}
          <Card {...ownershipSummaryCardProps}>
            <Illustration
              brand={brand}
              name="saved-credit-cards"
              width={71}
              alt="Credit card illustration"
            />
            <br />
            <div className="mk-contracts__summary-card-content">
              <h3 className="caption-1">{myContracts.paymentsCard.labels.title}</h3>
              <div className="mk-contracts__summary-card-body body-1">{myContracts.paymentsCard.description}</div>
            </div>
            <div className="mk-contracts__summary-card-footer">
              <ZinButton
                variant="primary"
                size="small"
                onClick={() => window.open(PAYMENTUS_URL, '_blank')}>
                {myContracts.paymentsCard.labels.cta}
              </ZinButton>
            </div>
          </Card>
          <Card {...ownershipSummaryCardProps}>
            <Illustration
              brand={brand}
              name="case"
              width={80}
              alt="Suitecase illustration"
            />
            <br />
            <h3 className="caption-1">{myContracts.pointOfContactCard.labels.title}</h3>
            <div className="mk-contracts__summary-card-body body-1">
              {myContracts.pointOfContactCard.description}
              <br />
              <a href={`tel:${ownershipPageConfig.pointOfContact.supportPhone}`}>{ownershipPageConfig.pointOfContact.supportPhone}</a>
              <br />
              <br />
              <b>{myContracts.pointOfContactCard.labels.owner}</b>
              <div className="summary-card__body-value body-2">{toTitleCase(owner.name)}</div>
              <br />
              <b>{myContracts.pointOfContactCard.labels.address}</b>
              {owner.addressLine1 && <div className="summary-card__body-value body-2">{toTitleCase(owner.addressLine1)}</div>}
              {owner.addressLine2 && <div className="summary-card__body-value body-2">{toTitleCase(owner.addressLine2)}</div>}
              {owner.addressLine3 && <div className="summary-card__body-value body-2">{toTitleCase(owner.addressLine3)}</div>}
              {getFormattedCityStatePostal() && <div className="summary-card__body-value body-2">{getFormattedCityStatePostal()}</div>}
              <br />
              <b>{myContracts.pointOfContactCard.labels.email}</b>
              <div className="summary-card__body-value body-2">{owner.emailAddress}</div>
              <br />
              <b>{myContracts.pointOfContactCard.labels.phone}</b>
              {owner.phoneHome?.phoneNumber && <div className="summary-card__body-value body-2">{formatPhoneNumber(owner.phoneHome.phoneNumber)}</div>}
              {owner.phoneMobile?.phoneNumber && <div className="summary-card__body-value body-2">{formatPhoneNumber(owner.phoneMobile.phoneNumber)}</div>}
              {owner.phoneWork?.phoneNumber && <div className="summary-card__body-value body-2">{formatPhoneNumber(owner.phoneWork.phoneNumber)}</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default MkContracts
